import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Button, Col, Form } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import CreatableSelect from 'react-select/creatable';
import { MdDelete } from 'react-icons/md';
import TemplateModel from '../components/templateModal';
import SearchTemplateModal from '../components/searchTemplateModal';
import VisitsDymanicTable from './visits-dynamic-table';
import { getAllVisits, getComplains, getFrequencies, getSeverities, getDurations } from '../../../../redux/visit';
import { createTemplate, getTemplates, updateTemplateItems, getTemplateById } from '../../../../redux/template';
import useDebounce from '../../../../utils/useDebounceHook';
import { optionsStyle, visitInitialValues } from '../composables';

const ComplaintDropdown = ({ options, value, onChange, onSearch }) => (
	<Form.Group as={Col}>
		<CreatableSelect
			menuPortalTarget={document.body}
			styles={optionsStyle}
			isClearable
			options={options}
			value={value}
			onChange={onChange}
			onInputChange={(inputValue, { action }) => {
				if (action === 'input-change') {
					onSearch(inputValue);
				}
			}}
			isValidNewOption={(inputValue, _selectValue, selectOptions) =>
				!!inputValue.trim() &&
				!selectOptions.some(
					(opt) => opt.label.toLowerCase() === inputValue.trim().toLowerCase()
				)
			}
			filterOption={(option, rawInput) =>
				(!value || value.label.toLowerCase() !== option.label.toLowerCase()) &&
				option.label.toLowerCase().includes(rawInput.toLowerCase())
			}
		/>
	</Form.Group>
);

const DateInput = ({ value, onChange }) => (
	<Form.Control
		type="date"
		className="editable-cell p-1 w-100"
		placeholder="YYYY-MM-DD"
		value={value || ''}
		format="YYYY-MM-DD"
		onChange={(e) => onChange(e.target.value)}
	/>
);

const Complaints = ({ customTitle, isHideIcons = false, onClose = () => { }, isFromSettings = false, search = '' }) => {
	const { values, setFieldValue, errors, touched, setFieldError, setFieldTouched } = useFormikContext();
	const [errorMessage, setErrorMessage] = useState([]);
	const [model, setModel] = useState(false);
	const [searchModel, setSearchModel] = useState(false);
	const { complains, frequencies, severities, durations } = useSelector((state) => state.visits);
	const { list: templates } = useSelector((state) => state.templates);
	const [searchTerms, setSearchTerms] = useState({
		complaint: '',
		frequency: '',
		severity: '',
		duration: ''
	});
	const debouncedSearch = useDebounce(searchTerms, 500);
	const [complaintOptions, setComplaintOptions] = useState([]);
	const dispatch = useDispatch();
	const params = useParams();
	const [frequencyOptions, setFrequencyOptions] = useState([]);
	const [severityOptions, setSeverityOptions] = useState([]);
	const [durationOptions, setDurationOptions] = useState([]);

	useEffect(() => {
		if (isFromSettings) {
			setFieldValue('complaints', [...visitInitialValues.complaints]);
		}
	}, []);

	const handleValueChange = (index, field, value, newValue) => {
		const updatedComplaints = values.complaints.map((item) => ({ ...item }));
		updatedComplaints[index][field] = value;
		if (field === 'complaintId') {
			updatedComplaints[index]['complaintName'] = newValue;
		}
		if (field === 'frequencyId') {
			updatedComplaints[index]['frequencyName'] = newValue;
		}
		if (field === 'severityId') {
			updatedComplaints[index]['severityName'] = newValue;
		}
		if (field === 'durationId') {
			updatedComplaints[index]['durationName'] = newValue;
		}
		setFieldValue('complaints', updatedComplaints);
	};

	const handleNewComplaint = (index, complaintName) => {
		const isDuplicate = complaintOptions.some(
			opt => opt.label.toLowerCase() === complaintName.toLowerCase()
		) || values.complaints.some(
			complaint => complaint.complaintName?.toLowerCase() === complaintName.toLowerCase()
		);

		if (isDuplicate) {
			toast.error('This complaint already exists or is selected.');
			return;
		}

		const tempId = `temp-${Date.now()}`;
		const newOption = { label: complaintName, value: tempId };
		setComplaintOptions(prev => [...prev, newOption]);
		handleValueChange(index, 'complaintId', tempId, complaintName);
	};

	const handleNewFrequency = (index, frequencyName) => {

		const isDuplicate = frequencyOptions.some(
			opt => opt.label.toLowerCase() === frequencyName.toLowerCase()
		) || values.complaints.some(
			complaint => complaint.frequencyName?.toLowerCase() === frequencyName.toLowerCase()
		);

		if (isDuplicate) {
			toast.error('This frequency already exists or is selected.');
			return;
		}

		const tempId = `temp-${Date.now()}`;
		const newOption = { label: frequencyName, value: tempId };
		setFrequencyOptions(prev => [...prev, newOption]);
		handleValueChange(index, 'frequencyId', tempId, frequencyName);
	};

	const handleNewSeverity = (index, severityName) => {

		const isDuplicate = severityOptions.some(
			opt => opt.label.toLowerCase() === severityName.toLowerCase()
		) || values.complaints.some(
			complaint => complaint.severityName?.toLowerCase() === severityName.toLowerCase()
		);

		if (isDuplicate) {
			toast.error('This severity already exists or is selected.');
			return;
		}

		const tempId = `temp-${Date.now()}`;
		const newOption = { label: severityName, value: tempId };
		setSeverityOptions(prev => [...prev, newOption]);
		handleValueChange(index, 'severityId', tempId, severityName);
	};

	const handleNewDuration = (index, durationName) => {

		const isDuplicate = durationOptions.some(
			opt => opt.label.toLowerCase() === durationName.toLowerCase()
		) || values.complaints.some(
			complaint => complaint.durationName?.toLowerCase() === durationName.toLowerCase()
		);

		if (isDuplicate) {
			toast.error('This duration already exists or is selected.');
			return;
		}

		const tempId = `temp-${Date.now()}`;
		const newOption = { label: durationName, value: tempId };
		setDurationOptions(prev => [...prev, newOption]);
		handleValueChange(index, 'durationId', tempId, durationName);
	};

	const addNewRow = () => {
		const newRow = {
			id: values.complaints.length + 1,
			complaintId: '',
			frequencyId: '',
			severityId: '',
			durationId: '',
			date: ''
		};
		setFieldValue('complaints', [...values.complaints, newRow]);
		setErrorMessage([]);
	};

	const handleDelete = (index) => {
		const updatedComplaints = values.complaints.filter((_, i) => i !== index);
		setFieldValue('complaints', updatedComplaints);
	};

	const handleSearchChange = (type) => (value) => {
		setSearchTerms((prev) => ({ ...prev, [type]: value }));
	};

	const mergeOptions = (setState) => (items) => {
		if (!items?.length) return;
		const newOptions = items.map(item => ({
			label: item.name,
			value: item.id
		}));
		setState(prevOptions => {
			const existingIds = new Set(prevOptions.map(opt => opt.value));
			const merged = [...prevOptions];
			newOptions.forEach(opt => {
				if (!existingIds.has(opt.value)) {
					merged.push(opt);
				}
			});
			return merged;
		});
	};

	const handleSelectTemplate = (option) => {
		dispatch(getTemplateById({ id: option.value })).unwrap().then(res => {
			setSearchModel(false);
			const fetchedComplaints = res.response.data.data.complaintItems;
			const newComplaints = fetchedComplaints.map((complaint, index) => ({
				id: index + 1,
				complaintId: complaint.complaint_id,
				frequencyId: complaint.frequency_id,
				severityId: complaint.severity_id,
				durationId: complaint.duration_id,
				date: moment(complaint.date).format('YYYY-MM-DD')
			}));
			setFieldValue('complaints', newComplaints);
			if (newComplaints.length > 0 && typeof errors.complaints === 'string') {
				setFieldError('complaints', undefined);
				setFieldTouched('complaints', false);
			}
		});
	};

	const validateComplaints = () => {
		const errors = [];
		if (values.complaints.length === 0) {
			errors.push('Please add at least one complaint');
		} else {
			values.complaints.forEach((complaint, index) => {
				if (!complaint.complaintId) {
					errors.push(`Row ${index + 1}: Complaint is required`);
				}
				if (!complaint.frequencyId) {
					errors.push(`Row ${index + 1}: Frequency is required`);
				}
				if (!complaint.severityId) {
					errors.push(`Row ${index + 1}: Severity is required`);
				}
				if (!complaint.durationId) {
					errors.push(`Row ${index + 1}: Duration is required`);
				}
				if (!complaint.date) {
					errors.push(`Row ${index + 1}: Date is required`);
				}
			});
		}
		if (errors.length > 0) {
			setErrorMessage(errors);
			return false;
		} else {
			setErrorMessage([]);
			return true;
		}
	};

	const saveTemplate = async() => {
		if (validateComplaints()) {
			const existingTemplate = values.complaints.find(complaint => complaint.existingTemplateId);
			const existingTemplateId = existingTemplate?.existingTemplateId;
			const payload = {
				templateId: existingTemplateId || null,
				complaints: values.complaints.map(complaint => {
					let complaintData = {};

					if (typeof complaint.complaintId === 'string' && complaint.complaintId.startsWith('temp-')) {
						complaintData = { complaintName: complaint.complaintName };
					} else if (typeof complaint.complaintId === 'number') {
						complaintData = { ...complaintData, complaintId: complaint.complaintId };
					}
					if (typeof complaint.frequencyId === 'string' && complaint.frequencyId.startsWith('temp-')) {
						complaintData = { frequencyName: complaint.frequencyName };
					} else if (typeof complaint.frequencyId === 'number') {
						complaintData = { ...complaintData, frequencyId: complaint.frequencyId };
					}
					if (typeof complaint.severityId === 'string' && complaint.severityId.startsWith('temp-')) {
						complaintData = { severityName: complaint.severityName };
					} else if (typeof complaint.severityId === 'number') {
						complaintData = { ...complaintData, severityId: complaint.severityId };
					}
					if (typeof complaint.durationId === 'string' && complaint.durationId.startsWith('temp-')) {
						complaintData = { ...complaintData, durationName: complaint.durationName };
					} else if (typeof complaint.durationId === 'number') {
						complaintData = { ...complaintData, durationId: complaint.durationId };
					}
					return {
						...complaintData,
						id: complaint.existingTemplateItemId || null,
						date: complaint.date
					};
				})
			};
			if (existingTemplateId) {
				await dispatch(updateTemplateItems(payload)).unwrap().then(() => {
					dispatch(getTemplates({ type: 'complaint', search: search, per_page: 10, pId: Number(params.id) }));
					onClose();
				}).catch((err) => {
					toast.error(err.message || 'Something went wrong');
				});
			} else {
				setModel(true);
			}
		} else {
			return;
		}
	};

	const submitTemplate = async(name) => {
		const payload = {
			pId: Number(params.id),
			name: name,
			type: 'complaint',
			complaints: values.complaints.map(complaint => {
				let complaintData = {};

				if (typeof complaint.complaintId === 'string' && complaint.complaintId.startsWith('temp-')) {
					complaintData = { ...complaintData, complaintName: complaint.complaintName };
				} else if (typeof complaint.complaintId === 'number') {
					complaintData = { ...complaintData, complaintId: complaint.complaintId };
				}
				if (typeof complaint.frequencyId === 'string' && complaint.frequencyId.startsWith('temp-')) {
					complaintData = { ...complaintData, frequencyName: complaint.frequencyName };
				} else if (typeof complaint.frequencyId === 'number') {
					complaintData = { ...complaintData, frequencyId: complaint.frequencyId };
				}
				if (typeof complaint.severityId === 'string' && complaint.severityId.startsWith('temp-')) {
					complaintData = { ...complaintData, severityName: complaint.severityName };
				} else if (typeof complaint.severityId === 'number') {
					complaintData = { ...complaintData, severityId: complaint.severityId };
				}
				if (typeof complaint.durationId === 'string' && complaint.durationId.startsWith('temp-')) {
					complaintData = { ...complaintData, durationName: complaint.durationName };
				} else if (typeof complaint.durationId === 'number') {
					complaintData = { ...complaintData, durationId: complaint.durationId };
				}
				return {
					...complaintData,
					date: complaint.date
				};
			})
		};
		await dispatch(createTemplate(payload)).unwrap().then(() => {
			dispatch(getTemplates({ type: 'complaint', search: search, per_page: 10, pId: Number(params?.id || 0) }));
			setModel(false);
			onClose();
		}).catch((err) => {
			toast.error(err.message || 'Something went wrong');
		});
	};

	const openSearchTemplate = () => {
		setSearchModel(true);
		dispatch(getTemplates({ type: 'complaint', search: search, per_page: 10, pId: Number(params?.id || 0) }));
	};

	const handleLoadLastVisit = () => {
		dispatch(getAllVisits({ pId: Number(params?.id || 0), type: 'all', page: 1, per_page: 1 })).unwrap().then(res => {
			setSearchModel(false);
			const visits = res.response.data.data.data;
			if (visits.length === 0) {
				return;
			}
			const fetchedComplaints = visits[0].complaints;
			const newComplaints = fetchedComplaints && fetchedComplaints.length > 0 ? fetchedComplaints.map((complaint, index) => ({
				id: index + 1,
				complaintId: complaint.complaint_id,
				frequencyId: complaint.frequency_id,
				severityId: complaint.severity_id,
				durationId: complaint.duration_id,
				date: complaint.date ? moment(complaint.date).format('YYYY-MM-DD') : '',
			})) : visitInitialValues.complaints;
			setFieldValue('complaints', newComplaints);
			if (newComplaints.length > 0 && typeof errors.complaints === 'string') {
				setFieldError('complaints', undefined);
				setFieldTouched('complaints', false);
			}
		});
	};

	const columns = [
		{
			name: '#',
			selector: (_, index) => index + 1,
			width: '50px',
		},
		{
			name: t('VISIT.COMPLAINTS'),
			cell: (row, index) => {
				const selectedIds = values.complaints
					.filter((_, i) => i !== index)
					.map(c => c.complaintId);

				const filteredOptions = complaintOptions.filter(
					opt => !selectedIds.includes(opt.value)
				);
				return (
					<ComplaintDropdown
						options={filteredOptions}
						value={filteredOptions.find(opt => opt.value === row.complaintId) || ''}
						onChange={(option) => {
							if (option?.__isNew__) {
								handleNewComplaint(index, option.label);
							} else {
								handleValueChange(index, 'complaintId', option?.value || '');
							}
						}}
						onSearch={handleSearchChange('complaint')}
					/>
				);
			},
			width: '300px',
		},
		{
			name: t('VISIT.FREQUENCY'),
			cell: (row, index) => {
				// const selectedIds = values.complaints
				// 	.filter((_, i) => i !== index)
				// 	.map(c => c.frequencyId);

				// const filteredOptions = frequencyOptions.filter(
				// 	opt => !selectedIds.includes(opt.value)
				// );
				return (
					<ComplaintDropdown
						options={frequencyOptions}
						value={frequencyOptions.find(opt => opt.value === row.frequencyId) || ''}
						onChange={(option) => {
							if (option?.__isNew__) {
								handleNewFrequency(index, option.label);
							} else {
								handleValueChange(index, 'frequencyId', option?.value || '');
							}
						}}
						onSearch={handleSearchChange('frequency')}
					/>
				);
			},
			width: '200px',
		},
		{
			name: t('VISIT.SEVERITY'),
			cell: (row, index) => {
				return (
					<ComplaintDropdown
						options={severityOptions}
						value={severityOptions.find(opt => opt.value === row.severityId) || ''}
						onChange={(option) => {
							if (option?.__isNew__) {
								handleNewSeverity(index, option.label);
							} else {
								handleValueChange(index, 'severityId', option?.value || '');
							}
						}}
						onSearch={handleSearchChange('severity')}
					/>
				);
			},
			width: '200px',
		},
		{
			name: t('VISIT.DURATION'),
			cell: (row, index) => {
				return (
					<ComplaintDropdown
						options={durationOptions}
						value={durationOptions.find(opt => opt.value === row.durationId) || ''}
						onChange={(option) => {
							if (option?.__isNew__) {
								handleNewDuration(index, option.label);
							} else {
								handleValueChange(index, 'durationId', option?.value || '');
							}
						}}
						onSearch={handleSearchChange('duration')}
					/>
				);
			},
			width: '200px',
		},
		{
			name: t('VISIT.DATE'),
			cell: (row, index) => (
				<DateInput
					value={row.date}
					onChange={(value) => handleValueChange(index, 'date', value)}
				/>
			),
		},
		{
			name: t('CFORMS.COLUMNS.ACTIONS'),
			cell: (_row, index) => (
				<Button title={t('GENERAL.DELETE')} size="sm" variant='danger' onClick={() => handleDelete(index)}>
					<MdDelete size={18} />
				</Button>
			),
			width: '80px',
		},
	];

	useEffect(() => {
		mergeOptions(setComplaintOptions)(complains);
	}, [complains]);

	useEffect(() => {
		mergeOptions(setFrequencyOptions)(frequencies);
	}, [frequencies]);

	useEffect(() => {
		mergeOptions(setSeverityOptions)(severities);
	}, [severities]);

	useEffect(() => {
		mergeOptions(setDurationOptions)(durations);
	}, [durations]);

	useEffect(() => {
		const { complaint, frequency, severity, duration } = debouncedSearch;
		if (complaint) {
			dispatch(getComplains({ search: debouncedSearch, per_page: 5, status: 'active' }));
		}
		if (frequency) {
			dispatch(getFrequencies({ search: debouncedSearch, per_page: 5, status: 'active' }));
		}
		if (severity) {
			dispatch(getSeverities({ search: debouncedSearch, per_page: 5, status: 'active' }));
		}
		if (duration) {
			dispatch(getDurations({ search: debouncedSearch, per_page: 5, status: 'active' }));
		}
	}, [debouncedSearch, dispatch]);

	return (
		<>
			<VisitsDymanicTable
				title={customTitle}
				columns={columns}
				data={values.complaints || []}
				addRow={addNewRow}
				saveTemplate={saveTemplate}
				searchTemplate={openSearchTemplate}
				errorMessage={errorMessage}
				handleDelete={handleDelete}
				loadLastVisit={handleLoadLastVisit}
				isHideIcons={isHideIcons}
			/>
			<TemplateModel
				show={model}
				close={() => setModel(false)}
				submitTemplate={submitTemplate}
			/>
			<SearchTemplateModal
				show={searchModel}
				close={() => setSearchModel(false)}
				type="Complaint"
				options={templates}
				handleSelectTemplate={handleSelectTemplate}
				pId={Number(params.id)}
				isFromSettings={isFromSettings}
			/>
			{typeof errors.complaints === 'string' && touched.complaints && (
				<Form.Text className="text-danger mb-2 d-block">
					{errors.complaints}
				</Form.Text>
			)}
		</>
	);
};

export default Complaints;
